/** Keybindings: every action with its default key, the player's overrides
 *  (saved), and matching against key events. The game, replays, button key
 *  hints and Settings > Controls all read from here. */

export type Context = "battle" | "replay" | "both";

export interface Key {
  /** `KeyboardEvent.code` (layout-independent: the key's position). */
  code: string;
  ctrl?: boolean;
  alt?: boolean;
}

export interface Action {
  id: string;
  label: string;
  group: string;
  context: Context;
  key: Key;
}

const k = (code: string, ctrl = false): Key => ({ code, ctrl });

/** Every bindable action, in the order Settings lists them. */
export const ACTIONS: Action[] = [
  // Orders (BAR's keys).
  { id: "fight", label: "Fight", group: "Orders", context: "battle", key: k("KeyF") },
  { id: "patrol", label: "Patrol", group: "Orders", context: "battle", key: k("KeyP") },
  { id: "stop", label: "Stop", group: "Orders", context: "battle", key: k("KeyS") },
  { id: "buildMenu", label: "Build menu (builders selected)", group: "Orders", context: "battle", key: k("KeyB") },
  { id: "ride", label: "Ride a train (then click a station)", group: "Orders", context: "battle", key: k("KeyR") },
  { id: "prospectArea", label: "Prospect an area (prospectors; then drag a box)", group: "Orders", context: "battle", key: k("KeyI") },
  { id: "repeat", label: "Repeat queue", group: "Orders", context: "battle", key: k("KeyT") },
  { id: "fireState", label: "Fire state", group: "Orders", context: "battle", key: k("KeyL") },
  { id: "moveState", label: "Move state", group: "Orders", context: "battle", key: k("Semicolon") },
  { id: "policy", label: "Policy editor", group: "Orders", context: "battle", key: k("KeyK") },
  // Selection.
  { id: "sameType", label: "Same type in view", group: "Selection", context: "battle", key: k("KeyQ") },
  { id: "sameTypeAll", label: "Same type everywhere", group: "Selection", context: "battle", key: k("KeyW", true) },
  { id: "allUnits", label: "All units", group: "Selection", context: "battle", key: k("KeyE", true) },
  { id: "holdTypes", label: "Box keeps to selected types (hold)", group: "Selection", context: "battle", key: k("KeyZ") },
  { id: "holdIdle", label: "Box keeps to idle units (hold)", group: "Selection", context: "battle", key: k("Space") },
  { id: "idleEngineer", label: "Next idle engineer", group: "Selection", context: "battle", key: k("KeyB", true) },
  { id: "cancelBuild", label: "Cancel selected construction (Backspace too)", group: "Orders", context: "battle", key: k("Delete") },
  // Production.
  { id: "research", label: "Research", group: "Screens", context: "battle", key: k("F6") },
  { id: "designs", label: "Designs", group: "Screens", context: "battle", key: k("F7") },
  { id: "market", label: "Market", group: "Screens", context: "battle", key: k("F8") },
  { id: "production", label: "Production graphs", group: "Screens", context: "battle", key: k("F9") },
  { id: "diplomacy", label: "Diplomacy", group: "Screens", context: "battle", key: k("F10") },
  { id: "build1", label: "Build slot 1", group: "Production", context: "battle", key: k("KeyZ") },
  { id: "build2", label: "Build slot 2", group: "Production", context: "battle", key: k("KeyX") },
  { id: "build3", label: "Build slot 3", group: "Production", context: "battle", key: k("KeyC") },
  { id: "build4", label: "Build slot 4", group: "Production", context: "battle", key: k("KeyV") },
  { id: "build5", label: "Build slot 5", group: "Production", context: "battle", key: k("KeyB") },
  { id: "copy", label: "Copy barracks settings", group: "Production", context: "battle", key: k("KeyC", true) },
  { id: "paste", label: "Paste barracks settings", group: "Production", context: "battle", key: k("KeyV", true) },
  // Camera.
  { id: "overview", label: "Whole map / back", group: "Camera", context: "both", key: k("Tab") },
  { id: "follow", label: "Follow selected unit", group: "Camera", context: "replay", key: k("KeyF") },
  // Replay.
  { id: "playPause", label: "Play / pause", group: "Replay", context: "replay", key: k("Space") },
  { id: "stepBack", label: "Step back", group: "Replay", context: "replay", key: k("ArrowLeft") },
  { id: "stepForward", label: "Step forward", group: "Replay", context: "replay", key: k("ArrowRight") },
  { id: "toStart", label: "Go to start", group: "Replay", context: "replay", key: k("Home") },
  { id: "toEnd", label: "Go to end", group: "Replay", context: "replay", key: k("End") },
  { id: "slower", label: "Slower", group: "Replay", context: "replay", key: k("Minus") },
  { id: "faster", label: "Faster", group: "Replay", context: "replay", key: k("Equal") },
  // View.
  { id: "tint1", label: "Tint: team", group: "View", context: "replay", key: k("Digit1") },
  { id: "tint2", label: "Tint: type", group: "View", context: "replay", key: k("Digit2") },
  { id: "tint3", label: "Tint: out of ammo", group: "View", context: "replay", key: k("Digit3") },
  { id: "tint4", label: "Tint: suppressed", group: "View", context: "replay", key: k("Digit4") },
  { id: "tint5", label: "Tint: wounded", group: "View", context: "replay", key: k("Digit5") },
  { id: "tint6", label: "Tint: dug in", group: "View", context: "replay", key: k("Digit6") },
  { id: "fade", label: "Next fade", group: "View", context: "replay", key: k("KeyQ") },
  { id: "layer.units", label: "Layer: units", group: "View", context: "replay", key: k("KeyU") },
  { id: "layer.fire", label: "Layer: fire", group: "View", context: "replay", key: k("KeyX") },
  { id: "layer.shells", label: "Layer: shells", group: "View", context: "replay", key: k("KeyS") },
  { id: "layer.trains", label: "Layer: trains", group: "View", context: "replay", key: k("KeyT") },
  { id: "layer.track", label: "Layer: rail", group: "View", context: "replay", key: k("KeyR") },
  { id: "layer.depots", label: "Layer: depots", group: "View", context: "replay", key: k("KeyD") },
  { id: "layer.corridors", label: "Layer: corridors", group: "View", context: "replay", key: k("KeyC") },
  { id: "layer.effects", label: "Layer: effects", group: "View", context: "replay", key: k("KeyE") },
  { id: "layer.zones", label: "Layer: zones", group: "View", context: "replay", key: k("KeyZ") },
  { id: "layer.grid", label: "Layer: hex grid", group: "View", context: "replay", key: k("KeyG") },
  { id: "layer.minimap", label: "Layer: minimap", group: "View", context: "replay", key: k("KeyM") },
  { id: "layer.territory", label: "Layer: territory", group: "View", context: "replay", key: k("KeyY") },
  { id: "layer.intel", label: "Layer: prospecting", group: "View", context: "replay", key: k("KeyP") },
  { id: "layer.flows", label: "Layer: goods flow", group: "View", context: "replay", key: k("KeyO") },
];

/** Fixed, listed for reference: not rebindable. */
export const FIXED: [string, string][] = [
  ["Ctrl + 1–9 / 1–9", "Set / select a control group"],
  ["Ctrl + F1–F4 / F1–F4", "Set / jump to a camera spot"],
  ["Arrows, middle-drag, wheel", "Pan, zoom"],
  ["Shift + order", "Queue"],
  ["Esc", "Clear selection; with none, the game menu"],
];

const STORE = "ferro-keys";
const byId = new Map(ACTIONS.map((a) => [a.id, a]));
let overrides: Record<string, Key> = {};
try {
  overrides = JSON.parse(localStorage.getItem(STORE) ?? "{}");
} catch {
  overrides = {};
}
const listeners: (() => void)[] = [];

export function binding(id: string): Key {
  return overrides[id] ?? byId.get(id)!.key;
}

export function setBinding(id: string, key: Key | null): void {
  if (key === null) delete overrides[id];
  else overrides[id] = key;
  try {
    localStorage.setItem(STORE, JSON.stringify(overrides));
  } catch {
    /* this session only */
  }
  listeners.forEach((f) => f());
}

export function resetBindings(): void {
  overrides = {};
  try {
    localStorage.removeItem(STORE);
  } catch {
    /* fine */
  }
  listeners.forEach((f) => f());
}

/** Calls `f` on every change; returns the way to stop. */
export function onBindingsChange(f: () => void): () => void {
  listeners.push(f);
  return () => {
    const i = listeners.indexOf(f);
    if (i >= 0) listeners.splice(i, 1);
  };
}

/** Whether `e` is action `id`'s key (Shift is free: it queues). */
export function is(id: string, e: KeyboardEvent): boolean {
  const b = binding(id);
  return e.code === b.code && e.ctrlKey === !!b.ctrl && e.altKey === !!b.alt;
}

/** Whether a key-up releases action `id`'s key (held actions). */
export function released(id: string, e: KeyboardEvent): boolean {
  return e.code === binding(id).code;
}

const NAMES: Record<string, string> = {
  Space: "Space",
  Escape: "Esc",
  Tab: "Tab",
  Semicolon: ";",
  Minus: "−",
  Equal: "+",
  Comma: ",",
  Period: ".",
  Slash: "/",
  Backslash: "\\",
  Quote: "'",
  BracketLeft: "[",
  BracketRight: "]",
  Backquote: "`",
  ArrowLeft: "←",
  ArrowRight: "→",
  ArrowUp: "↑",
  ArrowDown: "↓",
  Home: "Home",
  End: "End",
  Delete: "Del",
  Insert: "Ins",
  PageUp: "PgUp",
  PageDown: "PgDn",
  Enter: "Enter",
  Backspace: "Bksp",
};

/** A key's short name: "F", "Ctrl+E", ";". */
export function keyName(key: Key): string {
  const base = NAMES[key.code] ?? key.code.replace(/^Key/, "").replace(/^Digit/, "").replace(/^Numpad/, "Num ");
  return `${key.ctrl ? "Ctrl+" : ""}${key.alt ? "Alt+" : ""}${base}`;
}

/** The short hint for a button's corner (Ctrl as ^). */
export function hint(id: string): string {
  return keyName(binding(id)).replace("Ctrl+", "^").replace("Alt+", "⌥");
}

/** Actions sharing `id`'s key where both can fire (same context, or "both"). */
export function conflicts(id: string): Action[] {
  const a = byId.get(id)!;
  const b = binding(id);
  return ACTIONS.filter((o) => {
    if (o.id === id) return false;
    const ob = binding(o.id);
    const same = ob.code === b.code && !!ob.ctrl === !!b.ctrl && !!ob.alt === !!b.alt;
    const overlap = a.context === "both" || o.context === "both" || a.context === o.context;
    // Build slots act only with barracks selected; holds only while box-selecting.
    const exclusive = (x: Action) => x.group === "Production" && x.id.startsWith("build");
    const hold = (x: Action) => x.id.startsWith("hold");
    if (exclusive(a) !== exclusive(o) || hold(a) !== hold(o)) return false;
    return same && overlap;
  });
}
